'use client';

/** PDP Shopify options + nested CMS blocks (`variant_buttons`, `variant_swatches`). */

import { useMemo } from 'react';
import { useProduct } from '@/contexts/ProductContext';
import VariantButtons from '@/components/blocks/variantOptions/variantButtons/VariantButtons';
import VariantSwatches from '@/components/blocks/variantOptions/variantSwatches/VariantSwatches';
import {
  resolveVariantButtonsOptionName,
  resolveVariantSwatchesOptionName,
} from '@/components/blocks/variantOptions/shopifyOptionConventions';
import { getSelectedOptionValue, optionNamesMatch } from '@/utils/product/product-context-helpers';

/** Finds child entry by `_content_type_uid` on grouped reference or modular row. */
function resolveVariantChild(block, contentTypeUid) {
  if (!block) return null;
  if (block._content_type_uid === contentTypeUid) return block;
  const row = block.variants?.find((v) => v._content_type_uid === contentTypeUid);
  return row ? (row.reference?.[0] ?? row) : null;
}

const VariantOptions = ({ block, position }) => {
  const { selectedVariant, updateOptions, productData } = useProduct();
  const shopifyProduct = productData?.shopifyProduct;
  const options = useMemo(() => shopifyProduct?.options ?? [], [shopifyProduct]);

  const { variantButtonsBlock, variantSwatchesBlock } = useMemo(() => ({
    variantButtonsBlock: resolveVariantChild(block, 'variant_buttons'),
    variantSwatchesBlock: resolveVariantChild(block, 'variant_swatches'),
  }), [block]);

  const variantButtonsOptionName = variantButtonsBlock?.variant_options?.length
    ? resolveVariantButtonsOptionName(variantButtonsBlock)
    : null;
  const variantSwatchesOptionName = variantSwatchesBlock?.variants?.length
    ? resolveVariantSwatchesOptionName(variantSwatchesBlock)
    : null;

  /**
   * Native `<select>` list: drop cooling when cards render it via `variant_buttons`;
   * drop color when swatches render it via `variant_swatches`.
   */
  const dropdownOptions = useMemo(() => {
    const excludedOptionNames = [
      ...(variantButtonsOptionName ? [variantButtonsOptionName] : []),
      ...(variantSwatchesOptionName ? [variantSwatchesOptionName] : []),
    ];
    return options.filter(
      (o) =>
        !excludedOptionNames.some((ex) => optionNamesMatch(ex, o.name)),
    );
  }, [variantButtonsOptionName, variantSwatchesOptionName, options]);

  if (!options.length) return null;

  return (
    <div className="flex flex-col gap-6">
      {dropdownOptions.map((option) => (
        <OptionSelect
          key={option.name}
          option={option}
          position={position}
          selectedVariant={selectedVariant}
          updateOptions={updateOptions}
        />
      ))}
      {variantButtonsBlock ? (
        <VariantButtons block={variantButtonsBlock} resolvedOptionName={variantButtonsOptionName} />
      ) : null}
      {variantSwatchesBlock ? (
        <VariantSwatches block={variantSwatchesBlock} resolvedOptionName={variantSwatchesOptionName} />
      ) : null}
    </div>
  );
};


/** One Shopify option rendered as labeled `<select>`; matches names case-insensitively vs `selectedOptions`. */
const OptionSelect = ({ option, position, selectedVariant, updateOptions }) => {
  const optionKey = (option.name || 'option').replace(/\s+/g, '-').toLowerCase();
  const selectId = `variant-option-${position}-${optionKey}`;

  const currentValue = getSelectedOptionValue(selectedVariant, option.name);

  return (
    <div className="flex flex-col gap-3 bg-white">
      <label
        htmlFor={selectId}
        className="block cursor-pointer font-sans text-sm font-semibold text-primary"
      >
        {option.name}
      </label>
      <div className="relative">
        <select
          id={selectId}
          data-cy={`option-dropdown-${optionKey}`}
          value={currentValue}
          onChange={(e) => {
            updateOptions([{ name: option.name, value: e.target.value }], { persist: true });
            e.target.blur();
          }}
          className="w-full cursor-pointer appearance-none border-2 border-sand bg-white px-4 py-4 font-sans text-base text-primary outline-none transition-[box-shadow,border-color] focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary sm:text-xl md:hover:border-primary md:hover:ring-1 md:hover:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {option.values.map((value) => (
            <option key={value} value={value} className="text-base text-primary sm:text-xl">
              {value}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#4487cc]">
          <ChevronIcon />
        </div>
      </div>
    </div>
  );
};

const ChevronIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default VariantOptions;
